// The text of a pull request's files at its head, for code shown outside
// the diff view (a suggestion is compared against it).
import { blob, contentAt } from '../api/gh';
import { LruMap } from '../api/cache';
import type { ConversationView, ThreadPage } from './rangeHost';

/** Recent files by repository, head and path. A failed read is not kept, so the next asks again. */
const memo = new LruMap<string, Promise<string[] | null>>(50);

/**
 * The lines of `path` at the PR's head; null when the file is not there or
 * cannot be read. A changed file is read by its blob SHA (the PR's file
 * list), any other through the contents API at the head commit. A read
 * that fails resolves to null and is tried again on the next call.
 */
export function headLines(pr: Pick<ConversationView, 'owner' | 'repo' | 'headRefOid'> & Pick<ThreadPage, 'files' | 'filesHead'>, path: string): Promise<string[] | null> {
  const head = pr.filesHead ?? pr.headRefOid;
  if (!head) return Promise.resolve(null);
  const key = `${pr.owner}/${pr.repo}@${head}:${path}`;
  let p = memo.get(key);
  if (!p) {
    const file = pr.files?.find((f) => f.filename === path);
    const text =
      file?.sha && file.status !== 'removed'
        ? blob(pr.owner, pr.repo, file.sha).then((b) => b.text)
        : contentAt(pr.owner, pr.repo, path, head);
    const read = text.then((t) => (t === null ? null : (t.endsWith('\n') ? t.slice(0, -1) : t).split('\n')));
    p = read.catch(() => {
      if (memo.get(key) === p) memo.delete(key);
      return null;
    });
    memo.set(key, p);
  }
  return p;
}
